/**
 * Parses links that point back into our own app — the web URLs that "Copy link"
 * produces (https://hp-chat-web.web.app/...) and the mobile app's native scheme
 * (hp-chat-v1://...). Mirrors hp-chat-v1's src/shared/utils/appLinks.ts.
 *
 *   File:         /files/<folderId>?file=<fileId>
 *   Folder:       /files/<folderId>
 *   Announcement: /?announcement=<rowId>  or  /announcement/<rowId>
 *
 * Only our host and our scheme match; any other domain returns null.
 */

export type AppLink =
  | { type: "announcement"; rowId: number }
  | { type: "files"; folderId: string; fileId?: string };

export const APP_WEB_ORIGIN = "https://hp-chat-web.web.app";
const APP_HOST = "hp-chat-web.web.app";
const NATIVE_SCHEME_PREFIX = /^hp-chat-v1:\/\//i;

/** Our links anywhere in a piece of text (web URL or native scheme). */
export const APP_LINK_PATTERN =
  /(?:https?:\/\/hp-chat-web\.web\.app|hp-chat-v1:\/)\/[^\s<>()[\]]+/gi;

/**
 * hp-chat-v1://files/x → https://hp-chat-web.web.app/files/x. Browsers can't
 * open the native scheme, so the web URL is the one to link to.
 */
export const toAppWebUrl = (url: string): string =>
  url.replace(NATIVE_SCHEME_PREFIX, `${APP_WEB_ORIGIN}/`);

export const parseAppLink = (url: string | null | undefined): AppLink | null => {
  if (!url) return null;

  let parsed: URL;
  try {
    parsed = new URL(toAppWebUrl(url.trim()));
  } catch {
    return null;
  }
  if (!/^https?:$/.test(parsed.protocol) || parsed.hostname.toLowerCase() !== APP_HOST) {
    return null;
  }

  const segments = parsed.pathname.split("/").filter(Boolean);

  // /?announcement=123
  if (segments.length === 0) {
    const rowId = parsed.searchParams.get("announcement");
    return rowId && /^\d+$/.test(rowId)
      ? { type: "announcement", rowId: Number(rowId) }
      : null;
  }

  // /announcement/123
  if (segments.length === 2 && segments[0] === "announcement" && /^\d+$/.test(segments[1])) {
    return { type: "announcement", rowId: Number(segments[1]) };
  }

  // /files/<folderId>?file=<fileId>
  if (segments.length === 2 && segments[0] === "files") {
    let folderId = segments[1];
    try {
      folderId = decodeURIComponent(folderId);
    } catch {
      // keep the raw segment
    }
    const fileId = parsed.searchParams.get("file");
    return { type: "files", folderId, ...(fileId ? { fileId } : {}) };
  }

  return null;
};

// ── App-wide notice ──────────────────────────────────────────────────────────
// A link can fail after the page that handled it has gone (e.g. a folder link
// that sends the user back to chat), so failures are shown by <AppLinkNotice />
// in AppLayout rather than by the page itself.

const NOTICE_EVENT = "hp:app-link-notice";

export const showAppLinkNotice = (message: string) => {
  window.dispatchEvent(new CustomEvent<string>(NOTICE_EVENT, { detail: message }));
};

export const onAppLinkNotice = (listener: (message: string) => void) => {
  const handler = (e: Event) => listener((e as CustomEvent<string>).detail);
  window.addEventListener(NOTICE_EVENT, handler);
  return () => window.removeEventListener(NOTICE_EVENT, handler);
};
